import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import businessService from '../services/business'
import { useAuth } from './AuthContext'

const LocationsContext = createContext()

export const useLocations = () => {
  const context = useContext(LocationsContext)
  if (!context) {
    throw new Error('useLocations must be used within a LocationsProvider')
  }
  return context
}

export const LocationsProvider = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const [locations, setLocations] = useState([])
  const [selectedLocationId, setSelectedLocationId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastFetch, setLastFetch] = useState(null)
  const hasFetchedRef = useRef(false)

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000

  const fetchLocations = useCallback(async (forceRefresh = false) => {
    setLoading(true)
    setError(null)

    try {
      const fetchedLocations = await businessService.getBusinessLocations()
      setLocations(fetchedLocations || [])
      
      // Set the first location as default if available and none is selected
      if (fetchedLocations && fetchedLocations.length > 0 && !selectedLocationId) {
        setSelectedLocationId(fetchedLocations[0].location_id)
      }
      
      setLastFetch(Date.now())
      return fetchedLocations || []
    } catch (err) {
      console.error('Failed to load user locations:', err)
      setError(err.message || 'Failed to load locations')
      setLocations([])
      throw err
    } finally {
      setLoading(false)
    }
  }, [selectedLocationId]) // Only depend on selectedLocationId

  const refreshLocations = useCallback(async () => {
    setLastFetch(null) // Force refresh
    return fetchLocations(true)
  }, [fetchLocations])

  // Initialize locations only when user is authenticated
  useEffect(() => {
    // Don't fetch if auth is still loading or user is not authenticated
    if (authLoading || !isAuthenticated) {
      setLoading(false)
      hasFetchedRef.current = false
      return
    }

    // Don't fetch if we already have fetched for this session
    if (hasFetchedRef.current) {
      return
    }

    let mounted = true
    hasFetchedRef.current = true
    
    const initializeLocations = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const fetchedLocations = await businessService.getBusinessLocations()
        
        if (mounted) {
          setLocations(fetchedLocations || [])
          
          // Set the first location as default if available and none is selected
          if (fetchedLocations && fetchedLocations.length > 0 && !selectedLocationId) {
            setSelectedLocationId(fetchedLocations[0].location_id)
          }
          
          setLastFetch(Date.now())
        }
      } catch (err) {
        if (mounted) {
          console.error('Failed to load user locations:', err)
          setError(err.message || 'Failed to load locations')
          setLocations([])
          hasFetchedRef.current = false // Reset on error so it can retry
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }
    
    initializeLocations()
    
    return () => {
      mounted = false
    }
  }, [isAuthenticated, authLoading]) // Only depend on auth state

  // Clear locations when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setLocations([])
      setSelectedLocationId(null)
      setLastFetch(null)
      setError(null)
      setLoading(false)
      hasFetchedRef.current = false // Reset fetch flag
    }
  }, [isAuthenticated])

  const value = {
    locations,
    selectedLocationId,
    setSelectedLocationId,
    loading,
    error,
    fetchLocations,
    refreshLocations,
    hasLocations: locations.length > 0
  }

  return (
    <LocationsContext.Provider value={value}>
      {children}
    </LocationsContext.Provider>
  )
}

export default LocationsContext